<template>
  <div data-component="apps-integration">
    <or-button
      data-button="open-apps-integration-options"
      :loading="isInitializing"
      :disabled="isInitializing"
      @click="onOpenPopup"
    >
      <or-icon :icon="icon" />
      {{ openButtonLabel }}
    </or-button>

    <or-modal
      data-popup="apps-integration"
      :is-open="isPopupOpen"
      @close="isPopupOpen = false"
    >
      <template #header>
        {{ $t('HITL.app-launcher.apps-integration.apps-options-title') }}
      </template>

      <template #default>
        <apps-integration-preview
          v-if="content === 'preview'"
          :app="app"
          :qr-code-url="qrCodeUrl"
          @qr-code-click="$emit('qr-code-click')"
        />

        <apps-integration-options
          v-if="content === 'update'"
          ref="apps-integration-options"
          :app="app"
          :account="account"
          :env="env"
        />
      </template>

      <template #footer>
        <div class="actions">
          <or-button
            v-if="optionsType === 'update'"
            :color="activateButtonColor"
            :disabled="isUpdating"
            :loading="isActivating"
            @click="onToggleActivate"
          >
            {{ activateButtonLabel }}
          </or-button>

          <div class="actions-block">
            <or-button
              color="primary"
              variant="outlined"
              :disabled="isUpdating || isActivating"
              @click="onCancel"
            >
              {{ $t('HITL.common.actions.cancel') }}
            </or-button>

            <or-button
              color="primary"
              :loading="isUpdating"
              :disabled="!isSuccessButtonEnabled || isActivating"
              @click="onSuccess"
            >
              {{ successButtonLabel }}
            </or-button>
          </div>
        </div>
      </template>
    </or-modal>
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from 'vue-demi';
import { useVuelidate } from '@vuelidate/core';
import {
  OrButtonV3 as OrButton,
  OrIconV3 as OrIcon,
  OrModalV3 as OrModal,
} from '@onereach/ui-components-25';

import AppsIntegrationOptions from './AppsIntegrationOptions.vue';
import AppsIntegrationPreview from './AppsIntegrationPreview.vue';
import { OneReachApp } from '../../../types';

export default defineComponent({
  name: 'AppsIntegration',
  components: {
    OrButton,
    OrIcon,
    OrModal,
    AppsIntegrationOptions,
    AppsIntegrationPreview,
  },
  props: {
    app: {
      type: Object as PropType<OneReachApp>,
      default: () => ({}),
    },
    account: {
      type: String,
      default: '',
    },
    env: {
      type: String,
      default: '',
    },
    qrCodeUrl: {
      type: String,
      default: '',
    },
    isInitializing: Boolean,
    isUpdating: Boolean,
    isActivating: Boolean,
  },
  emits: ['create', 'update'],
  setup() {
    return { $v: useVuelidate() };
  },
  data() {
    return {
      isPopupOpen: false,
      content: !_.isEmpty(this.app) ? 'preview' : 'update',
    };
  },
  computed: {
    isAppReady() {
      return !_.isEmpty(this.app);
    },

    optionsType() {
      return this.isAppReady ? 'update' : 'create';
    },

    icon() {
      return this.isAppReady ? 'computer' : 'new_releases';
    },

    openButtonLabel() {
      const OPEN_SETTINGS_LABEL = this.$t('HITL.app-launcher.apps-integration.open-apps-active-options');
      return this.isAppReady
        ? this.app.isActive
          ? OPEN_SETTINGS_LABEL
          : `${OPEN_SETTINGS_LABEL} [${this.$t('HITL.common.status.offline')}]`
        : this.$t('HITL.app-launcher.apps-integration.open-apps-initial-options');
    },

    activateButtonLabel() {
      return this.app.isActive
        ? this.$t('HITL.common.actions.deactivate')
        : this.$t('HITL.common.actions.activate');
    },

    activateButtonColor() {
      return this.app.isActive ? 'danger' : 'primary';
    },

    successButtonLabel() {
      return this.isAppReady
        ? this.content === 'preview'
          ? this.$t('HITL.common.actions.edit')
          : this.$t('HITL.common.actions.update')
        : this.$t('HITL.common.actions.create');
    },

    isSuccessButtonEnabled() {
      return !this.$v.$errors.length;
    },
  },
  watch: {
    /* App changes on user actions like "Create" or "Update" */
    app(current, previous) {
      if (!_.isEqual(current, previous)) {
        this.content = 'preview';
      }
    },
  },
  methods: {
    onOpenPopup() {
      this.isPopupOpen = true;
      this.content = this.isAppReady ? 'preview' : 'update';
    },

    onCancel() {
      this.isAppReady
        ? this.content === 'update'
          ? this.content = 'preview'
          : this.isPopupOpen = false
        : this.isPopupOpen = false;
    },

    onSuccess() {
      if (this.content === 'preview') {
        return (this.content = 'update');
      }

      const ref = this.$refs['apps-integration-options'] as any;
      this.$emit(this.optionsType, ref.options);
    },

    onToggleActivate() {
      const update = { ...this.app, isActive: !this.app.isActive };
      this.$emit('update', update);
    },
  },
});
</script>

<style lang="scss">
[data-component='apps-integration'] {
  width: 40rem;

  .or-button-v3 {
    width: 100%;
  }
}

[data-popup='apps-integration'] {
  .actions {
    display: flex;
    gap: var(--s-5);

    .actions-block {
      display: flex;
      gap: var(--s-4);
      margin-left: auto;
    }
  }
}
</style>
